import { useLayoutEffect, useRef } from 'react';

import type { CadenceStepTypePure } from '../CGStep/types';
import { getBlockStepItemsWithStatus } from '../CampaignDayCard/blockStepRules';
import { cn } from '../cn';
import Button from '../Input2/Button';
import StepIconSvg from '../StepIcon/StepIconSvg';
import { General } from '../StrokeIcons';

export type TemplateStepPickerPureProps = {
  /** Step types already present in the block — used to disable once-only types. */
  existingTypes: readonly CadenceStepTypePure[];
  /**
   * Extra per-type disabled reasons supplied by the host (e.g. when a
   * team plan can't run a particular step type yet).
   */
  disabledTypeReasons?: Partial<Record<CadenceStepTypePure, string>>;
  onPickType: (type: CadenceStepTypePure) => void;
  onCancel?: () => void;
  className?: string;
  optionSize?: 'sm' | 'md';
  /** When true, voice_note step type is available in the picker. */
  voiceEnabled?: boolean;
  /** When true, the `call` step type is available in the picker (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
  /**
   * Touch campaign lock: when set, the picker offers ONLY this step type — the
   * template is a touch campaign restricted to a single step type.
   */
  restrictToType?: CadenceStepTypePure | null;
};

const TYPE_DESCRIPTIONS: Record<string, string> = {
  connect: 'LinkedIn connection request',
  message: 'LinkedIn message',
  inmail: 'LinkedIn InMail',
  likeRecentPosts: 'Like a recent post',
  follow: 'Follow profile',
  email: 'Send an email',
  reminder: 'Manual task reminder',
  call: 'Phone call',
  voice_note: 'Send a voice note',
};

/**
 * Step type picker for the template editor's delay blocks. Reuses the
 * same `blockStepRules` allow-list and once-only logic as the campaign
 * picker, so a step type that's allowed once per day on the contact
 * detail view is also allowed once per delay block here.
 */
export function TemplateStepPickerPure({
  existingTypes,
  disabledTypeReasons,
  onPickType,
  onCancel,
  className,
  optionSize = 'md',
  voiceEnabled,
  voiceCallingEnabled,
  restrictToType,
}: TemplateStepPickerPureProps) {
  const items = getBlockStepItemsWithStatus(existingTypes, disabledTypeReasons, restrictToType).filter(
    ({ item }) => (voiceEnabled || item.value !== 'voice_note') && (voiceCallingEnabled || item.value !== 'call'),
  );

  // Touch campaign shortcut: when the template is locked to a single step type
  // (`restrictToType`) there's nothing to choose — offering a one-button picker
  // is a pointless extra click. If exactly one addable (enabled) option remains,
  // auto-pick it. Fires in a layout effect (before paint) so the picker never
  // flashes, and once per mount via a ref guard. When the sole option is disabled
  // (a once-only type already present) we fall through and render it so the user
  // sees why it can't be added.
  const autoPickType =
    restrictToType != null && items.length === 1 && !items[0]!.disabled ? items[0]!.item.value : null;
  const pickedRef = useRef(false);
  useLayoutEffect(() => {
    if (autoPickType != null && !pickedRef.current) {
      pickedRef.current = true;
      onPickType(autoPickType);
    }
  }, [autoPickType, onPickType]);
  if (autoPickType != null) return null;

  return (
    <div data-testid="template-step-picker" className={cn('flex w-full flex-col px-2 py-3', className)}>
      <div className="mb-2 flex items-center justify-between">
        <span
          className="text-xs font-semibold uppercase tracking-wide text-lynch-700 dark:text-lynch-300"
          data-testid="template-step-picker-heading">
          Add a step
        </span>
        {onCancel != null && (
          <Button
            data-testid="template-step-picker-cancel"
            icon={<General.XCircle />}
            size="xs"
            text
            color="gray"
            onClick={onCancel}>
            cancel
          </Button>
        )}
      </div>
      {items.length === 0 && (
        <p
          className="px-1 py-2 text-xs italic text-lynch-600 dark:text-lynch-400"
          data-testid="template-step-picker-empty">
          No more step types can be added to this block.
        </p>
      )}
      {items.length > 0 && (
        <div
          className="flex flex-wrap gap-1"
          role="group"
          aria-label="Step types"
          data-testid="template-step-picker-options">
          {items.map(({ item, disabled, disabledReason }) => {
            const description = TYPE_DESCRIPTIONS[item.value];
            return (
              <Button
                key={item.value}
                data-testid={`template-step-picker-option-${item.value}`}
                data-step-type={item.value}
                data-disabled-reason={disabled ? 'once-only-already-added' : undefined}
                aria-label={item.label}
                aria-disabled={disabled || undefined}
                readOnly={disabled}
                className={cn(disabled && 'cursor-not-allowed opacity-50')}
                tooltip={
                  <span>
                    <span className="font-semibold">{item.label}</span>
                    {description && (
                      <>
                        <br />
                        <span className="text-xs opacity-80">{description}</span>
                      </>
                    )}
                    {disabled && disabledReason && (
                      <>
                        <br />
                        <span className="mt-1 inline-block text-xs italic text-amber-200 dark:text-yellow-800">
                          {disabledReason}
                        </span>
                      </>
                    )}
                  </span>
                }
                tooltipPosition="top"
                icon={<StepIconSvg iconType={item.value} />}
                size={optionSize}
                color="gray"
                onClick={() => {
                  if (!disabled) onPickType(item.value);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
